import Button from '@alltrails/shared/denali/components/Button';
import useUser from 'hooks/useUser';
import { useEffect } from 'react';
import { FormattedMessage } from '@alltrails/shared/react-intl';
import Typography from 'components/Typography';
import classNames from 'classnames';
import useToggle from '@alltrails/shared/hooks/useToggle';
import logAdServed from '@alltrails/analytics/events/logAdServed';
import AdSource from '@alltrails/analytics/enums/AdSource';
import { wrapUrlSafe } from 'utils/language_support_util';
import useLanguageRegionCode from 'hooks/useLanguageRegionCode';
import * as styles from './Ad.module.scss';

type Props = {
  adUnitPath: string;
  className?: string;
  id: string;
};

// Desktop: https://www.figma.com/file/3dCQyKmNlOdfjaZ8vsDpzs/Native-Ads?node-id=464%3A140041&t=6m6YpR4rfbKIw3Ft-4
// Mobile: https://www.figma.com/file/3dCQyKmNlOdfjaZ8vsDpzs/Native-Ads?node-id=464%3A140300&t=6m6YpR4rfbKIw3Ft-4
export default function Ad({ adUnitPath, className, id }: Props) {
  const user = useUser();
  const [displayAd, toggleDisplayAd] = useToggle(false);
  const languageRegionCode = useLanguageRegionCode();

  useEffect(() => {
    if (!user?.pro) {
      const handleSlotRenderEnded = (event: googletag.events.SlotRenderEndedEvent) => {
        if (event.slot.getSlotElementId() === id && !event.isEmpty) {
          // TODO advertiser renamed advertiserID under GROW-602
          logAdServed({ advertiser: String(event.advertiserId), location: AdSource.TrailDetails });
          toggleDisplayAd();
        }
      };

      googletag.cmd.push(() => {
        const slot = googletag.defineSlot(adUnitPath, [[300, 250]], id).addService(googletag.pubads());

        if (__AT_DATA__.RAILS_ENV !== 'production') {
          slot.setTargeting('test', '1');
        }

        // this is mobile only so hide the ad >= 768
        const mapping = googletag.sizeMapping().addSize([768, 0], [0, 0]).addSize([0, 0], [300, 250]).build();
        slot.defineSizeMapping(mapping);

        googletag.enableServices();

        googletag.display(id);

        googletag.pubads().addEventListener('slotRenderEnded', handleSlotRenderEnded);
      });

      return () => {
        googletag.cmd.push(() => {
          googletag.pubads().removeEventListener('slotRenderEnded', handleSlotRenderEnded);
        });
      };
    }
  }, [adUnitPath, id, toggleDisplayAd, user?.pro]);

  return (
    <div className={classNames(styles.ad, className)} style={{ display: !displayAd && 'none' }}>
      <Typography className={styles.advertisement} color="secondary" variant="text100">
        <FormattedMessage defaultMessage="Advertisement" />
      </Typography>
      <div id={id} />
      <Button
        className={styles.button}
        linkInfo={{ href: wrapUrlSafe('/plus', languageRegionCode) }}
        size="sm"
        testId="go-ad-free"
        text={<FormattedMessage defaultMessage="Go ad free with AllTrails+" />}
        variant="primary"
      />
    </div>
  );
}
